/**
 * mockContactsDatabase.ts
 * 
 * Base de datos mock de contactos para simular el comportamiento
 * del futuro API REST. Esta estructura será reemplazada cuando
 * se implemente la base de datos real en WASP.
 * 
 * @version 3.0.1 - Geomarketing Phase 1
 * @date 2024-11-13
 * 
 * CHANGELOG v3.0.1:
 * - ✅ Agregados campos de geolocalización (address, location, locationStatus)
 * - ✅ Compatibilidad completa con estructura anterior
 * - ✅ Base de datos incremental híbrida
 */

// ==================== 📋 INTERFACES ====================

export interface ContactDB {
  id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  tags: string[];
  groups: string[];
  status: 'active' | 'inactive' | 'bounced' | 'unsubscribed';
  source: 'manual' | 'import' | 'api' | 'webform' | 'oneshot';
  positon?: string;
  created_at: string;
  updated_at: string;
  last_contacted?: string;
  total_messages_sent?: number;
  total_messages_received?: number;
  notes?: string;
  metadata?: Record<string, any>;
  custom_fields?: Record<string, any>;
  
  // ═══════════════════════════════════════════════════════════════════════
  // 🌍 GEOLOCALIZACIÓN (v3.0.1 - Geomarketing Phase 1)
  // ═══════════════════════════════════════════════════════════════════════
  // Campos opcionales para direcciones y referencias geográficas
  // Compatible con API REST futura para contexto LLM
  // Base de datos incremental híbrida - NO rompe compatibilidad
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Dirección postal del contacto
   * Usada para georreferencia del domicilio registrado
   */
  address?: {
    street?: string;        // Calle (ej: "Av. Libertador")
    number?: string;        // Número (ej: "1234")
    city?: string;          // Ciudad (ej: "Santiago")
    region?: string;        // Región/Estado (ej: "Metropolitana")
    country?: string;       // País (ej: "Chile")
    postalCode?: string;    // Código postal (ej: "8320000")
    fullAddress?: string;   // Dirección completa formateada
  };
  
  /**
   * Coordenadas geográficas del contacto
   * Puede venir de: registro, ubicación compartida, o manual
   */
  location?: {
    lat: number;           // Latitud (ej: -33.4489)
    lng: number;           // Longitud (ej: -70.6693)
    source: 'registration' | 'shared' | 'manual'; // Origen de coordenadas
    lastUpdate?: string;   // ISO timestamp última actualización
    accuracy?: number;     // Precisión en metros (opcional)
  };
  
  /**
   * Estado visual de ubicación para UI
   * - 'registered': Ubicación de domicilio (📍 verde)
   * - 'shared': Ubicación compartida via WhatsApp (📡 naranja)
   * - 'missing': Sin información GPS (⚠️ rojo)
   */
  locationStatus?: 'registered' | 'shared' | 'missing';
}

export interface ContactGroup {
  id: string;
  name: string;
  description?: string;
  contact_count: number;
  created_at: string;
  updated_at: string;
  color?: string;
  icon?: string;
}

export interface ContactTag {
  id: string;
  name: string;
  color: string;
  count: number;
}

export interface ContactStats {
  total: number;
  active: number;
  inactive: number;
  bounced: number;
  unsubscribed: number;
  new_this_week: number;
  new_this_month: number;
}

// ==================== 📊 DATOS MOCK ====================

export const MOCK_CONTACTS: ContactDB[] = [
  {
    id: '1',
    name: 'Juan Pérez',
    phone: '+56912345678',
    email: 'juan.perez@techcorp.cl',
    company: 'Tech Corp SpA',
    tags: ['vip', 'cliente', 'tecnologia'],
    groups: ['clientes-q1-2024', 'empresas-tech'],
    status: 'active',
    source: 'manual',
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-20T15:30:00Z',
    last_contacted: '2024-01-18T12:00:00Z',
    total_messages_sent: 15,
    total_messages_received: 12,
    notes: 'Cliente premium, interesado en soluciones cloud',
    // ✅ NUEVO: Geolocalización completa
    address: {
      street: 'Av. Providencia',
      number: '1234',
      city: 'Santiago',
      region: 'Metropolitana',
      country: 'Chile',
      postalCode: '7500000',
      fullAddress: 'Av. Providencia 1234, Santiago, Chile'
    },
    location: {
      lat: -33.4250,
      lng: -70.6100,
      source: 'registration',
      lastUpdate: '2024-01-15T10:00:00Z',
      accuracy: 10
    },
    locationStatus: 'registered'
  },
  {
    id: '2',
    name: 'María González',
    phone: '+56987654321',
    email: 'maria.gonzalez@innovatech.cl',
    company: 'InnovaTech Ltda',
    tags: ['lead', 'hot'],
    groups: ['leads-febrero', 'empresas-tech'],
    status: 'active',
    source: 'import',
    created_at: '2024-02-01T09:00:00Z',
    updated_at: '2024-02-05T14:20:00Z',
    last_contacted: '2024-02-03T11:30:00Z',
    total_messages_sent: 8,
    total_messages_received: 6,
    notes: 'Interesada en plan corporativo',
    // ✅ NUEVO: Solo ubicación compartida
    location: {
      lat: -33.4372,
      lng: -70.6506,
      source: 'shared',
      lastUpdate: '2024-02-03T11:30:00Z',
      accuracy: 50
    },
    locationStatus: 'shared'
  },
  {
    id: '3',
    name: 'Pedro Silva',
    phone: '+56911223344',
    email: 'pedro.silva@email.com',
    company: 'Consultora Silva',
    tags: ['consultor', 'networking'],
    groups: ['profesionales-independientes'],
    status: 'active',
    source: 'webform',
    created_at: '2024-01-20T14:00:00Z',
    updated_at: '2024-01-25T10:15:00Z',
    last_contacted: '2024-01-22T16:00:00Z',
    total_messages_sent: 5,
    total_messages_received: 4,
    // ✅ NUEVO: Sin ubicación
    locationStatus: 'missing'
  },
  {
    id: '4',
    name: 'Ana Martínez',
    phone: '+56922334455',
    email: 'ana.martinez@startup.cl',
    company: 'Startup Innovations',
    tags: ['startup', 'early-stage', 'tecnologia'],
    groups: ['startups-chile', 'leads-febrero'],
    status: 'active',
    source: 'api',
    created_at: '2024-02-10T11:00:00Z',
    updated_at: '2024-02-12T09:30:00Z',
    last_contacted: '2024-02-11T15:00:00Z',
    total_messages_sent: 3,
    total_messages_received: 2,
    notes: 'Fundadora, busca herramientas de automatización',
    // ✅ NUEVO: Dirección completa
    address: {
      fullAddress: 'Las Condes, Santiago, Chile'
    },
    location: {
      lat: -33.4080,
      lng: -70.5750,
      source: 'registration',
      lastUpdate: '2024-02-10T11:00:00Z'
    },
    locationStatus: 'registered'
  },
  {
    id: '5',
    name: 'Carlos Rodríguez',
    phone: '+56933445566',
    email: 'carlos.rodriguez@corp.com',
    company: 'Corporate Solutions',
    tags: ['corporativo', 'grande-empresa'],
    groups: ['empresas-grandes', 'clientes-q1-2024'],
    status: 'active',
    source: 'manual',
    created_at: '2024-01-05T08:00:00Z',
    updated_at: '2024-02-01T12:00:00Z',
    last_contacted: '2024-01-30T10:00:00Z',
    total_messages_sent: 20,
    total_messages_received: 18,
    notes: 'CTO, decisor técnico',
    // ✅ NUEVO: Ubicación manual
    location: {
      lat: -33.4489,
      lng: -70.6693,
      source: 'manual',
      lastUpdate: '2024-01-05T08:00:00Z'
    },
    locationStatus: 'registered'
  },
  {
    id: '6',
    name: 'Isabel Torres',
    phone: '+56944556677',
    email: 'isabel.torres@pyme.cl',
    company: 'PYME Textil',
    tags: ['pyme', 'retail'],
    groups: ['pymes-retail'],
    status: 'active',
    source: 'import',
    created_at: '2024-02-15T10:00:00Z',
    updated_at: '2024-02-16T11:00:00Z',
    total_messages_sent: 2,
    total_messages_received: 1,
    locationStatus: 'missing'
  },
  {
    id: '7',
    name: 'Roberto Fuentes',
    phone: '+56955667788',
    email: 'roberto.fuentes@consultor.com',
    tags: ['inactivo', 'seguimiento'],
    groups: ['profesionales-independientes'],
    status: 'inactive',
    source: 'webform',
    created_at: '2023-12-01T09:00:00Z',
    updated_at: '2023-12-15T10:00:00Z',
    last_contacted: '2023-12-10T14:00:00Z',
    total_messages_sent: 4,
    total_messages_received: 1,
    notes: 'No respondió últimas comunicaciones',
    locationStatus: 'missing'
  },
  {
    id: '8',
    name: 'Valentina Muñoz',
    phone: '+56966778899',
    email: 'valentina.munoz@ecommerce.cl',
    company: 'E-Commerce Pro',
    tags: ['ecommerce', 'online'],
    groups: ['sector-ecommerce', 'leads-febrero'],
    status: 'active',
    source: 'api',
    created_at: '2024-02-08T13:00:00Z',
    updated_at: '2024-02-10T15:00:00Z',
    last_contacted: '2024-02-09T11:00:00Z',
    total_messages_sent: 6,
    total_messages_received: 5,
    location: {
      lat: -33.4569,
      lng: -70.6483,
      source: 'shared',
      lastUpdate: '2024-02-09T11:00:00Z'
    },
    locationStatus: 'shared'
  },
  {
    id: '9',
    name: 'Diego Morales',
    phone: '+56977889900',
    email: 'diego.morales@bounce.test',
    company: 'Test Company',
    tags: ['problema-entrega'],
    groups: [],
    status: 'bounced',
    source: 'import',
    created_at: '2024-01-10T10:00:00Z',
    updated_at: '2024-01-25T12:00:00Z',
    last_contacted: '2024-01-20T09:00:00Z',
    total_messages_sent: 10,
    total_messages_received: 0,
    notes: 'Número fuera de servicio',
    locationStatus: 'missing'
  },
  {
    id: '10',
    name: 'Sofía Ramírez',
    phone: '+56988990011',
    email: 'sofia.ramirez@marketing.cl',
    company: 'Marketing Digital SPA',
    tags: ['marketing', 'agencia'],
    groups: ['agencias-marketing', 'clientes-q1-2024'],
    status: 'active',
    source: 'manual',
    created_at: '2024-01-12T11:00:00Z',
    updated_at: '2024-02-05T14:00:00Z',
    last_contacted: '2024-02-01T10:00:00Z',
    total_messages_sent: 12,
    total_messages_received: 10,
    notes: 'Directora de Marketing, muy receptiva',
    address: {
      fullAddress: 'Vitacura, Santiago, Chile'
    },
    location: {
      lat: -33.3900,
      lng: -70.5650,
      source: 'registration',
      lastUpdate: '2024-01-12T11:00:00Z'
    },
    locationStatus: 'registered'
  },
  {
    id: '11',
    name: 'Andrés Vargas',
    phone: '+56999001122',
    email: 'andres.vargas@finance.com',
    company: 'Financial Services',
    tags: ['finanzas', 'corporativo'],
    groups: ['sector-financiero', 'empresas-grandes'],
    status: 'active',
    source: 'webform',
    created_at: '2024-02-01T09:00:00Z',
    updated_at: '2024-02-14T16:00:00Z',
    last_contacted: '2024-02-12T11:00:00Z',
    total_messages_sent: 7,
    total_messages_received: 6,
    locationStatus: 'missing'
  },
  {
    id: '12',
    name: 'Carolina López',
    phone: '+56910112233',
    email: 'carolina.lopez@health.cl',
    company: 'HealthTech Solutions',
    tags: ['salud', 'tecnologia', 'innovacion'],
    groups: ['healthtech', 'startups-chile'],
    status: 'active',
    source: 'api',
    created_at: '2024-02-12T10:00:00Z',
    updated_at: '2024-02-15T12:00:00Z',
    last_contacted: '2024-02-14T09:00:00Z',
    total_messages_sent: 4,
    total_messages_received: 3,
    notes: 'Innovación en telemedicina',
    location: {
      lat: -33.4150,
      lng: -70.6050,
      source: 'shared',
      lastUpdate: '2024-02-14T09:00:00Z'
    },
    locationStatus: 'shared'
  },
  {
    id: '13',
    name: 'Fernando Soto',
    phone: '+56921223344',
    tags: ['sin-grupo'],
    groups: [],
    status: 'active',
    source: 'manual',
    created_at: '2024-02-16T14:00:00Z',
    updated_at: '2024-02-16T14:00:00Z',
    total_messages_sent: 0,
    total_messages_received: 0,
    locationStatus: 'missing'
  },
  {
    id: '14',
    name: 'Patricia Herrera',
    phone: '+56932334455',
    email: 'patricia.herrera@education.cl',
    company: 'EduTech Platform',
    tags: ['educacion', 'edtech'],
    groups: ['sector-educacion'],
    status: 'active',
    source: 'import',
    created_at: '2024-02-05T11:00:00Z',
    updated_at: '2024-02-08T15:00:00Z',
    last_contacted: '2024-02-07T10:00:00Z',
    total_messages_sent: 5,
    total_messages_received: 4,
    locationStatus: 'missing'
  },
  {
    id: '15',
    name: 'Miguel Castillo',
    phone: '+56943445566',
    email: 'miguel.castillo@logistics.com',
    company: 'Logistics Pro',
    tags: ['logistica', 'transporte'],
    groups: ['sector-logistica', 'pymes-retail'],
    status: 'active',
    source: 'webform',
    created_at: '2024-01-28T09:00:00Z',
    updated_at: '2024-02-10T13:00:00Z',
    last_contacted: '2024-02-08T11:00:00Z',
    total_messages_sent: 9,
    total_messages_received: 7,
    address: {
      fullAddress: 'Maipú, Santiago, Chile'
    },
    location: {
      lat: -33.5100,
      lng: -70.7600,
      source: 'registration',
      lastUpdate: '2024-01-28T09:00:00Z'
    },
    locationStatus: 'registered'
  }
];

export const MOCK_GROUPS: ContactGroup[] = [
  {
    id: 'clientes-q1-2024',
    name: 'Clientes Q1 2024',
    description: 'Clientes activos primer trimestre 2024',
    contact_count: 4,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-02-15T10:00:00Z',
    color: '#25D366',
    icon: '🎯'
  },
  {
    id: 'empresas-tech',
    name: 'Empresas Tech',
    description: 'Empresas del sector tecnológico',
    contact_count: 2,
    created_at: '2024-01-05T00:00:00Z',
    updated_at: '2024-02-10T12:00:00Z',
    color: '#128C7E',
    icon: '💻'
  },
  {
    id: 'leads-febrero',
    name: 'Leads Febrero 2024',
    description: 'Nuevos contactos captados en febrero',
    contact_count: 3,
    created_at: '2024-02-01T00:00:00Z',
    updated_at: '2024-02-16T14:00:00Z',
    color: '#34B7F1',
    icon: '🔥'
  },
  {
    id: 'profesionales-independientes',
    name: 'Profesionales Independientes',
    description: 'Consultores y freelancers',
    contact_count: 2,
    created_at: '2024-01-10T00:00:00Z',
    updated_at: '2024-01-25T11:00:00Z',
    color: '#FFA500',
    icon: '👤'
  },
  {
    id: 'startups-chile',
    name: 'Startups Chile',
    description: 'Emprendimientos tecnológicos',
    contact_count: 2,
    created_at: '2024-02-01T00:00:00Z',
    updated_at: '2024-02-12T10:00:00Z',
    color: '#9B59B6',
    icon: '🚀'
  },
  {
    id: 'empresas-grandes',
    name: 'Empresas Grandes',
    description: 'Corporaciones +100 empleados',
    contact_count: 2,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-02-01T15:00:00Z',
    color: '#2C3E50',
    icon: '🏢'
  },
  {
    id: 'pymes-retail',
    name: 'PYMEs Retail',
    description: 'Pequeñas y medianas empresas sector retail',
    contact_count: 2,
    created_at: '2024-02-01T00:00:00Z',
    updated_at: '2024-02-16T12:00:00Z',
    color: '#E74C3C',
    icon: '🛍️'
  },
  {
    id: 'sector-ecommerce',
    name: 'E-Commerce',
    description: 'Tiendas online y comercio electrónico',
    contact_count: 1,
    created_at: '2024-02-08T00:00:00Z',
    updated_at: '2024-02-10T16:00:00Z',
    color: '#3498DB',
    icon: '🛒'
  },
  {
    id: 'agencias-marketing',
    name: 'Agencias Marketing',
    description: 'Agencias de marketing digital',
    contact_count: 1,
    created_at: '2024-01-12T00:00:00Z',
    updated_at: '2024-02-05T14:00:00Z',
    color: '#E67E22',
    icon: '📊'
  },
  {
    id: 'sector-financiero',
    name: 'Sector Financiero',
    description: 'Empresas de servicios financieros',
    contact_count: 1,
    created_at: '2024-02-01T00:00:00Z',
    updated_at: '2024-02-14T17:00:00Z',
    color: '#27AE60',
    icon: '💰'
  },
  {
    id: 'healthtech',
    name: 'HealthTech',
    description: 'Tecnología aplicada a salud',
    contact_count: 1,
    created_at: '2024-02-12T00:00:00Z',
    updated_at: '2024-02-15T13:00:00Z',
    color: '#E91E63',
    icon: '⚕️'
  },
  {
    id: 'sector-educacion',
    name: 'Sector Educación',
    description: 'EdTech y plataformas educativas',
    contact_count: 1,
    created_at: '2024-02-05T00:00:00Z',
    updated_at: '2024-02-08T16:00:00Z',
    color: '#00BCD4',
    icon: '📚'
  },
  {
    id: 'sector-logistica',
    name: 'Logística & Transporte',
    description: 'Empresas de logística',
    contact_count: 1,
    created_at: '2024-01-28T00:00:00Z',
    updated_at: '2024-02-10T14:00:00Z',
    color: '#795548',
    icon: '🚚'
  }
];

export const MOCK_TAGS: ContactTag[] = [
  { id: 'vip', name: 'VIP', color: '#FFD700', count: 1 },
  { id: 'cliente', name: 'Cliente', color: '#25D366', count: 1 },
  { id: 'lead', name: 'Lead', color: '#34B7F1', count: 1 },
  { id: 'hot', name: 'Hot Lead', color: '#FF4500', count: 1 },
  { id: 'tecnologia', name: 'Tecnología', color: '#128C7E', count: 3 },
  { id: 'startup', name: 'Startup', color: '#9B59B6', count: 1 },
  { id: 'corporativo', name: 'Corporativo', color: '#2C3E50', count: 2 },
  { id: 'pyme', name: 'PYME', color: '#E74C3C', count: 1 },
  { id: 'ecommerce', name: 'E-Commerce', color: '#3498DB', count: 1 },
  { id: 'marketing', name: 'Marketing', color: '#E67E22', count: 1 },
  { id: 'finanzas', name: 'Finanzas', color: '#27AE60', count: 1 },
  { id: 'salud', name: 'Salud', color: '#E91E63', count: 1 },
  { id: 'educacion', name: 'Educación', color: '#00BCD4', count: 1 },
  { id: 'logistica', name: 'Logística', color: '#795548', count: 1 }
];

// ==================== 📊 FUNCIONES HELPER ====================

/**
 * Calcula estadísticas de los contactos
 */
export const getContactStats = (): ContactStats => {
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    total: MOCK_CONTACTS.length,
    active: MOCK_CONTACTS.filter(c => c.status === 'active').length,
    inactive: MOCK_CONTACTS.filter(c => c.status === 'inactive').length,
    bounced: MOCK_CONTACTS.filter(c => c.status === 'bounced').length,
    unsubscribed: MOCK_CONTACTS.filter(c => c.status === 'unsubscribed').length,
    new_this_week: MOCK_CONTACTS.filter(c => new Date(c.created_at) > oneWeekAgo).length,
    new_this_month: MOCK_CONTACTS.filter(c => new Date(c.created_at) > oneMonthAgo).length
  };
};

/**
 * Busca contactos por teléfono (para verificar duplicados)
 */
export const findContactByPhone = (phone: string): ContactDB | undefined => {
  const cleanPhone = phone.replace(/\s+/g, '').replace(/[^\d+]/g, '');
  return MOCK_CONTACTS.find(c => 
    c.phone.replace(/\s+/g, '').replace(/[^\d+]/g, '') === cleanPhone
  );
};

/**
 * Filtra contactos por grupo
 */
export const getContactsByGroup = (groupId: string): ContactDB[] => {
  return MOCK_CONTACTS.filter(c => c.groups.includes(groupId));
};

/**
 * Filtra contactos por tag
 */
export const getContactsByTag = (tagId: string): ContactDB[] => {
  return MOCK_CONTACTS.filter(c => c.tags.includes(tagId));
};

/**
 * Búsqueda de contactos
 */
export const searchContacts = (query: string): ContactDB[] => {
  const lowerQuery = query.toLowerCase();
  return MOCK_CONTACTS.filter(c => 
    c.name.toLowerCase().includes(lowerQuery) ||
    c.phone.includes(lowerQuery) ||
    c.email?.toLowerCase().includes(lowerQuery) ||
    c.company?.toLowerCase().includes(lowerQuery)
  );
};

/**
 * ✅ NUEVO: Filtrar contactos por estado de ubicación
 */
export const getContactsByLocationStatus = (
  status: 'registered' | 'shared' | 'missing'
): ContactDB[] => {
  return MOCK_CONTACTS.filter(c => c.locationStatus === status);
};

/**
 * ✅ NUEVO: Obtener contactos con ubicación válida
 */
export const getContactsWithLocation = (): ContactDB[] => {
  return MOCK_CONTACTS.filter(c => c.location && c.location.lat && c.location.lng);
};

export default {
  MOCK_CONTACTS,
  MOCK_GROUPS,
  MOCK_TAGS,
  getContactStats,
  findContactByPhone,
  getContactsByGroup,
  getContactsByTag,
  searchContacts,
  getContactsByLocationStatus,
  getContactsWithLocation
};